import Cookies from "js-cookie";
import React, { useEffect, useMemo } from "react";
import { toast } from "react-toastify";

import { CoreKeys } from "@intelligems/ig-plugin/core";
import { Launch } from "@src/components/Launch/Launch";
import { InstallPage } from "@src/domains/Install/InstallPage";
import { ShareApp } from "@src/ShareApp";
import { IgStorage } from "@src/utils/igStorage";
import { useIsMutating } from "@tanstack/react-query";

import { userKeys } from "../api/queryKeys";
import { useAuth } from "../hooks/auth";
import Loading from "../utils/Loading";

import { LoggedInRoutes } from "./loggedIn";
import { PublicRoutes } from "./public";

export const AppRoutes = () => {
  const user = useAuth(window.location.pathname !== "/install");
  const isLoggingIn = useIsMutating({ mutationKey: userKeys.all });

  useEffect(() => {
    window.enterBeta = () => {
      Cookies.set("ig-beta", "true", {
        expires: CoreKeys.ID_COOKIE_DAYS_TO_LIVE,
        domain: "." + window.location.hostname,
      });
    };
    window.exitBeta = () => {
      Cookies.remove("ig-beta");
    };
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const clear = params.get("clear");
    if (clear) {
      IgStorage.clearAuthTokens();
      url.searchParams.delete("clear");
      location.href = url.href;
    }

    const invalidAccessToken = params.get("invalidAccessToken");
    if (invalidAccessToken) {
      toast.error(
        "Shopify access token is invalid.  Please uninstall and reinstall the Intelligems app."
      );
    }

    // Set cookie if your-uncut-gems URL param exists and remove param
    const hasUncutGemsParam = params.has("your-uncut-gems");
    if (hasUncutGemsParam) {
      Cookies.set("open_uncut_gems", "true", {
        expires: 1 / 24, // Expires in 1 hour
      });
      url.searchParams.delete("your-uncut-gems");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const isLoggedIn = useMemo(
    () => user.isSuccess && user.data.type === "login" && user.data.user,
    [user.isSuccess, user.data]
  );

  if (window.location.pathname === "/launch") {
    return <Launch />;
  }

  if (window.location.pathname.startsWith("/share")) {
    // ShareRoute uses router hooks (useParams/useSearchParams), so it must be
    // rendered inside a Router. ShareApp supplies that RouterProvider (matching
    // the sibling LoggedInRoutes/PublicRoutes branches, which each own a router).
    return <ShareApp />;
  }

  if (window.location.pathname === "/install") {
    return <InstallPage />;
  }

  if (isLoggingIn || user.isLoading) {
    return <Loading centered={true} />;
  }

  if (isLoggedIn) {
    return <LoggedInRoutes />;
  }

  return <PublicRoutes />;
};
